import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/hooks";
import { ROUTES } from "routes/constants";

interface UseSubmittedApplicationGuardOptions {
    /**
     * Skips the guard for legitimate re-entry scenarios, e.g. a fresh GIWL
     * landing whose hydration resets the application before the flow resumes.
     */
    skip?: boolean;
}

/**
 * Blocks access to in-flow pages (quote, contact, payment, review) once the
 * application has reached a terminal decision page. When isApplicationSubmitted
 * is set, redirects to the Something Went Wrong page and returns true so the
 * caller can skip rendering. Starting a new flow clears the flag via
 * resetApplication / resetApplicationKeepOfferData.
 */
export const useSubmittedApplicationGuard = ({ skip = false }: UseSubmittedApplicationGuardOptions = {}): boolean => {
    const navigate = useNavigate();
    const isApplicationSubmitted = useAppSelector((state) => state.application.isApplicationSubmitted);
    const isBlocked = isApplicationSubmitted && !skip;

    useEffect(() => {
        if (isBlocked) {
            console.error("Blocked in-flow page access: application already submitted");
            navigate(ROUTES.SOMETHING_WENT_WRONG, { replace: true });
        }
    }, [isBlocked, navigate]);

    return isBlocked;
};

export default useSubmittedApplicationGuard;
